/**
 * Momentum-damped smooth scrolling.
 *
 * The reference site loads Lenis (its <html> carries the `lenis` class) plus
 * GSAP ScrollSmoother. That damping is a large part of why it feels different
 * from an ordinary page: the wheel sets a TARGET, and the actual scroll
 * position eases toward it over several frames instead of jumping.
 *
 * This is a small direct implementation rather than a dependency — the whole
 * behaviour is one lerp and a rAF loop, and the site is otherwise dependency
 * free.
 *
 * Deliberately NOT applied when:
 *   - the visitor prefers reduced motion (hijacking scroll is exactly the kind
 *     of motion that setting exists to prevent);
 *   - the input is touch (mobile browsers already have momentum, and overriding
 *     it makes a page feel broken and fights native overscroll).
 */

/*
 * Tuning.
 *
 * The first version used LERP 0.09, which felt laggy for a measurable reason:
 * at 9% per frame the opening frames move sub-pixel distances that
 * `window.scrollTo` rounds to zero, so the page sat visibly still for ~40ms
 * (about 6 frames) after the wheel before appearing to catch up. Profiling
 * showed rendering was never the issue — 0 dropped frames, 6.9ms average —
 * the easing curve was.
 *
 * 0.18 keeps a clear glide but starts moving on the first frame. The settle
 * threshold is 0.5px (below one device pixel) so the loop ends cleanly instead
 * of creeping.
 */
const LERP = 0.18;
const WHEEL_SCALE = 1.0;
const SETTLE = 0.5;

export function initSmoothScroll() {
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const coarse = window.matchMedia('(pointer: coarse)').matches;
  if (reduced || coarse) return null;

  /*
   * Take over BEFORE reading any position. `scroll-behavior: smooth` makes the
   * browser animate every scrollTo, which fights this loop frame for frame; the
   * companion rule in reference.css keys off this attribute. Set here rather
   * than at the end of setup so the very first wheel event is already clean.
   */
  document.documentElement.dataset.smooth = 'true';

  let target = window.scrollY;
  let current = window.scrollY;
  let running = false;
  let raf = 0;
  // Marks scroll events caused by this module, so onScroll can ignore them.
  let selfScroll = false;

  const maxScroll = () =>
    Math.max(0, document.documentElement.scrollHeight - window.innerHeight);

  const clamp = (v) => Math.max(0, Math.min(v, maxScroll()));

  function frame() {
    const diff = target - current;

    if (Math.abs(diff) < SETTLE) {
      current = target;
      selfScroll = true;
      window.scrollTo(0, Math.round(current));
      running = false;
      return;
    }

    /*
     * Guarantee at least 1px of movement per frame while easing.
     *
     * `window.scrollTo` snaps to whole pixels, so a frame that advances less
     * than 1px renders as no movement at all. Early frames of a gentle lerp do
     * exactly that, which is what produced the visible dead zone at the start
     * of every scroll.
     */
    const step = diff * LERP;
    const minStep = Math.sign(diff);
    current += Math.abs(step) < 1 ? minStep : step;

    selfScroll = true;
    window.scrollTo(0, Math.round(current));
    raf = requestAnimationFrame(frame);
  }

  function start() {
    if (running) return;
    running = true;
    // Start on THIS frame rather than waiting for the next callback — a rAF
    // scheduled from inside an event handler can otherwise miss the frame that
    // is already in flight, which shows up as a visible delay before movement.
    frame();
  }

  function onWheel(e) {
    // Let the browser handle zoom and horizontal intent untouched.
    if (e.ctrlKey || Math.abs(e.deltaX) > Math.abs(e.deltaY)) return;
    e.preventDefault();
    target = clamp(target + e.deltaY * WHEEL_SCALE);
    start();
  }

  /*
   * Keyboard, scrollbar drags and programmatic jumps bypass the wheel handler,
   * so the internal position has to re-sync from the real one.
   *
   * ⚠️ This must NOT run for our own scrollTo calls. Every frame of the loop
   * emits a scroll event, and re-reading `window.scrollY` there resets `target`
   * back to the current position — the animation cancels itself and the page
   * appears frozen for a beat before catching up. `selfScroll` marks the writes
   * this module makes so they are ignored here.
   */
  function onScroll() {
    if (selfScroll) {
      selfScroll = false;
      return;
    }
    if (!running) {
      target = window.scrollY;
      current = window.scrollY;
    }
  }

  function onKey(e) {
    const keys = ['PageDown', 'PageUp', 'Home', 'End', 'ArrowDown', 'ArrowUp', ' '];
    if (!keys.includes(e.key)) return;
    // Let the browser move focus and scroll, then pick up the new position.
    requestAnimationFrame(() => {
      target = window.scrollY;
      current = window.scrollY;
    });
  }

  /* Anchor links: ease to the target instead of jumping. CSS smooth-scroll is
     disabled while this module is active, since the two fight each other. */
  function onClick(e) {
    const link = e.target.closest('a[href^="#"]');
    if (!link) return;
    const id = link.getAttribute('href');
    if (!id || id === '#') return;
    const el = document.querySelector(id);
    if (!el) return;

    e.preventDefault();
    target = clamp(el.getBoundingClientRect().top + window.scrollY);
    start();
    // Keep the URL and focus behaviour of a normal anchor.
    history.pushState(null, '', id);
    el.setAttribute('tabindex', '-1');
    el.focus({ preventScroll: true });
  }

  window.addEventListener('wheel', onWheel, { passive: false });
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('keydown', onKey);
  document.addEventListener('click', onClick);
  window.addEventListener('resize', () => {
    target = clamp(target);
  });

  document.documentElement.dataset.smooth = 'true';

  return {
    destroy() {
      cancelAnimationFrame(raf);
      window.removeEventListener('wheel', onWheel);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('click', onClick);
      delete document.documentElement.dataset.smooth;
    },
  };
}
